import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { StatusBadge } from "@/components/domain/Bits";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Pencil, Users } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  superadmin: "Súper Admin", tenant_owner: "Propietario", admin: "Administrador",
  supervisor: "Supervisor", validator: "Validador", analyst: "Analista", seller: "Vendedor",
};
const EDITABLE_ROLES = ["admin", "supervisor", "validator", "analyst", "seller"];
const empty = { name: "", email: "", password: "", role: "seller", seller_code: "", status: "active" };

export default function TeamUsers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get("/admin/users").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { name: u.name, role: u.role, status: u.status === "suspended" ? "active" : "suspended" });
      toast.success(u.status === "suspended" ? "Usuario activado" : "Usuario suspendido");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo actualizar"); }
  };

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setF({ name: u.name || "", email: u.email, password: "", role: EDITABLE_ROLES.includes(u.role) ? u.role : "admin", seller_code: u.seller_code || "", status: u.status || "active" });
    setOpen(true);
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/users/${editing.user_id}`, f);
        toast.success("Usuario actualizado");
      } else {
        if (!f.email.trim()) { toast.error("Correo requerido"); return; }
        if (!f.password || f.password.length < 6) { toast.error("Contraseña mínima de 6 caracteres"); return; }
        await api.post("/admin/users", f);
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    }
  };

  return (
    <AdminLayout title="Usuarios y equipo">
      <div className="mb-6 flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm text-slate-500"><Users className="h-4 w-4 text-brand" />Gestiona a tu equipo: administradores, validadores y vendedores de tu empresa.</p>
        <Button onClick={openNew} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-user-btn"><UserPlus className="mr-1.5 h-4 w-4" />Nuevo usuario</Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr><th className="px-5 py-3">Nombre</th><th className="px-5 py-3">Correo</th><th className="px-5 py-3">Rol</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3">Alta</th><th className="px-5 py-3 text-right">Acciones</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.email}`}>
                <td className="px-5 py-3"><div className="font-semibold text-slate-900">{u.name}</div>{u.seller_code && <div className="font-mono text-xs text-slate-400">{u.seller_code}</div>}</td>
                <td className="px-5 py-3 text-slate-600">{u.email}</td>
                <td className="px-5 py-3"><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{ROLE_LABEL[u.role] || u.role}</span></td>
                <td className="px-5 py-3"><StatusBadge label={u.status === "suspended" ? "Suspendido" : "Activo"} cls={u.status === "suspended" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"} /></td>
                <td className="px-5 py-3 text-xs text-slate-400">{u.created_at ? fmtDateTime(u.created_at) : "—"}</td>
                <td className="px-5 py-3 text-right">
                  {["superadmin", "tenant_owner"].includes(u.role) ? <span className="text-xs text-slate-300">—</span> :
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="mr-1.5 h-3.5 w-3.5" />Editar</Button>
                      <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`toggle-user-${u.email}`}>{u.status === "suspended" ? "Activar" : "Suspender"}</Button>
                    </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>Los usuarios acceden con correo y contraseña. Solo administran datos de tu empresa.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-medium text-slate-500">Nombre</label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
            <div><label className="text-xs font-medium text-slate-500">Correo</label><Input type="email" value={f.email} disabled={!!editing} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>
            <div><label className="text-xs font-medium text-slate-500">{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" placeholder={editing ? "Dejar en blanco para no cambiar" : "Mínimo 6 caracteres"} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Rol</label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{EDITABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Estado</label>
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                  <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Activo</SelectItem><SelectItem value="suspended">Suspendido</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {f.role === "seller" && <div><label className="text-xs font-medium text-slate-500">Código de vendedor (opcional)</label><Input value={f.seller_code} onChange={(e) => setF({ ...f, seller_code: e.target.value })} data-testid="user-seller-code" placeholder="Se genera automáticamente si lo dejas vacío" /></div>}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-brand hover:bg-brand-hover" data-testid="save-user-btn">{editing ? "Guardar" : "Crear usuario"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
